import type { ColorPalette, FontPairingConfig } from "./types";

export const COLOR_PALETTES: Record<string, ColorPalette> = {
  "ocean-blue": {
    id: "ocean-blue",
    name: "Ocean Blue",
    primary: "#0369a1",
    primaryForeground: "#ffffff",
    secondary: "#0c4a6e",
    secondaryForeground: "#e0f2fe",
    accent: "#0369a1",
    accentForeground: "#ffffff",
    background: "#f8fafc",
    foreground: "#0f172a",
    muted: "#e2e8f0",
    mutedForeground: "#475569",
    border: "#cbd5e1",
    surfaceElevated: "rgba(255, 255, 255, 0.7)",
    surfaceTinted: "rgba(2, 132, 199, 0.05)",
    highlight: "rgba(2, 132, 199, 0.15)",
    textStrong: "#0f172a",
    textMuted: "#475569",
  },
  "oxford-navy": {
    id: "oxford-navy",
    name: "Oxford Navy & Gold",
    primary: "#1e3a8a",
    primaryForeground: "#ffffff",
    secondary: "#0f172a",
    secondaryForeground: "#fef3c7",
    accent: "#d97706",
    accentForeground: "#0f172a",
    background: "#fafaf9",
    foreground: "#1c1917",
    muted: "#e7e5e4",
    mutedForeground: "#78716c",
    border: "#d6d3d1",
    surfaceElevated: "rgba(255, 255, 255, 0.8)",
    surfaceTinted: "rgba(30, 58, 138, 0.04)",
    highlight: "rgba(217, 119, 6, 0.15)",
    textStrong: "#1c1917",
    textMuted: "#78716c",
  },
  "emerald-forest": {
    id: "emerald-forest",
    name: "Emerald Forest",
    primary: "#047857",
    primaryForeground: "#ffffff",
    secondary: "#064e3b",
    secondaryForeground: "#d1fae5",
    accent: "#065f46",
    accentForeground: "#ffffff",
    background: "#f8faf9",
    foreground: "#0f1f1a",
    muted: "#d1e7dd",
    mutedForeground: "#335c4b",
    border: "#a7d1bf",
    surfaceElevated: "rgba(255, 255, 255, 0.75)",
    surfaceTinted: "rgba(4, 120, 87, 0.06)",
    highlight: "rgba(4, 120, 87, 0.15)",
    textStrong: "#0f1f1a",
    textMuted: "#335c4b",
  },
  "royal-amethyst": {
    id: "royal-amethyst",
    name: "Royal Amethyst",
    primary: "#6d28d9",
    primaryForeground: "#ffffff",
    secondary: "#3b0764",
    secondaryForeground: "#e9d5ff",
    accent: "#5b21b6",
    accentForeground: "#ffffff",
    background: "#faf8ff",
    foreground: "#1a1025",
    muted: "#e4d8f0",
    mutedForeground: "#58436e",
    border: "#c4b2d6",
    surfaceElevated: "rgba(255, 255, 255, 0.8)",
    surfaceTinted: "rgba(109, 40, 217, 0.05)",
    highlight: "rgba(109, 40, 217, 0.2)",
    textStrong: "#1a1025",
    textMuted: "#58436e",
  },
  "crimson-slate": {
    id: "crimson-slate",
    name: "Harvard Crimson",
    primary: "#991b1b",
    primaryForeground: "#ffffff",
    secondary: "#450a0a",
    secondaryForeground: "#fee2e2",
    accent: "#b91c1c",
    accentForeground: "#ffffff",
    background: "#fef8f8",
    foreground: "#1c1011",
    muted: "#f5d5d8",
    mutedForeground: "#732c37",
    border: "#e5b5bc",
    surfaceElevated: "rgba(255, 255, 255, 0.85)",
    surfaceTinted: "rgba(153, 27, 27, 0.04)",
    highlight: "rgba(185, 28, 28, 0.15)",
    textStrong: "#1c1011",
    textMuted: "#732c37",
  },
  "amber-gold": {
    id: "amber-gold",
    name: "Academic Amber",
    primary: "#b45309",
    primaryForeground: "#ffffff",
    secondary: "#451a03",
    secondaryForeground: "#fef3c7",
    accent: "#d97706",
    accentForeground: "#291804",
    background: "#fffbeb",
    foreground: "#291804",
    muted: "#fde68a",
    mutedForeground: "#78350f",
    border: "#fcd34d",
    surfaceElevated: "rgba(255, 255, 255, 0.9)",
    surfaceTinted: "rgba(180, 83, 9, 0.05)",
    highlight: "rgba(180, 83, 9, 0.2)",
    textStrong: "#291804",
    textMuted: "#78350f",
  },
  "midnight-dark": {
    id: "midnight-dark",
    name: "Midnight Dark Mode",
    primary: "#38bdf8",
    primaryForeground: "#0f172a",
    secondary: "#1e293b",
    secondaryForeground: "#f8fafc",
    accent: "#38bdf8",
    accentForeground: "#0f172a",
    background: "#0b0f17",
    foreground: "#f1f5f9",
    muted: "#1e293b",
    mutedForeground: "#94a3b8",
    border: "#334155",
    isDark: true,
    surfaceElevated: "rgba(30, 41, 59, 0.7)",
    surfaceTinted: "rgba(56, 189, 248, 0.05)",
    highlight: "rgba(56, 189, 248, 0.25)",
    textStrong: "#f8fafc",
    textMuted: "#94a3b8",
  },
  "minimal-monochrome": {
    id: "minimal-monochrome",
    name: "Swiss Monochrome",
    primary: "#18181b",
    primaryForeground: "#ffffff",
    secondary: "#27272a",
    secondaryForeground: "#f4f4f5",
    accent: "#27272a",
    accentForeground: "#ffffff",
    background: "#ffffff",
    foreground: "#09090b",
    muted: "#f4f4f5",
    mutedForeground: "#52525b",
    border: "#e4e4e7",
    surfaceElevated: "rgba(255, 255, 255, 0.9)",
    surfaceTinted: "rgba(24, 24, 27, 0.03)",
    highlight: "rgba(39, 39, 42, 0.15)",
    textStrong: "#09090b",
    textMuted: "#52525b",
  },
  "aurora": {
    id: "aurora",
    name: "Aurora Boreal",
    primary: "#4338ca",
    primaryForeground: "#ffffff",
    secondary: "#fdf4ff",
    secondaryForeground: "#701a75",
    accent: "#db2777",
    accentForeground: "#ffffff",
    background: "#fafaf9",
    foreground: "#1c1917",
    muted: "#f3f4f6",
    mutedForeground: "#4b5563",
    border: "#e5e7eb",
    surfaceElevated: "rgba(255, 255, 255, 0.85)",
    surfaceTinted: "rgba(67, 56, 202, 0.03)",
    highlight: "linear-gradient(135deg, #4338ca 0%, #db2777 100%)",
    textStrong: "#111827",
    textMuted: "#4b5563",
  },
  "coastal": {
    id: "coastal",
    name: "Coastal Breeze",
    primary: "#0f766e",
    primaryForeground: "#ffffff",
    secondary: "#e0f2fe",
    secondaryForeground: "#0369a1",
    accent: "#e11d48",
    accentForeground: "#ffffff",
    background: "#f8fafc",
    foreground: "#0f172a",
    muted: "#f1f5f9",
    mutedForeground: "#475569",
    border: "#e2e8f0",
    surfaceElevated: "rgba(255, 255, 255, 0.75)",
    surfaceTinted: "rgba(15, 118, 110, 0.04)",
    highlight: "linear-gradient(135deg, #0f766e 0%, #0284c7 100%)",
    textStrong: "#0f172a",
    textMuted: "#475569",
  },
  "saffron": {
    id: "saffron",
    name: "Saffron Sunset",
    primary: "#b45309",
    primaryForeground: "#ffffff",
    secondary: "#fff1f2",
    secondaryForeground: "#9f1239",
    accent: "#be123c",
    accentForeground: "#ffffff",
    background: "#fffbeb",
    foreground: "#451a03",
    muted: "#fef3c7",
    mutedForeground: "#78350f",
    border: "#fde68a",
    surfaceElevated: "rgba(255, 255, 255, 0.9)",
    surfaceTinted: "rgba(180, 83, 9, 0.04)",
    highlight: "linear-gradient(135deg, #b45309 0%, #be123c 100%)",
    textStrong: "#451a03",
    textMuted: "#78350f",
  },
  "graphite-luxe": {
    id: "graphite-luxe",
    name: "Graphite & Champagne",
    primary: "#e5c07b",
    primaryForeground: "#121214",
    secondary: "#2c2c30",
    secondaryForeground: "#f3f4f6",
    accent: "#e5c07b",
    accentForeground: "#121214",
    background: "#121214",
    foreground: "#f3f4f6",
    muted: "#2c2c30",
    mutedForeground: "#a1a1aa",
    border: "#3f3f46",
    isDark: true,
    surfaceElevated: "rgba(44, 44, 48, 0.7)",
    surfaceTinted: "rgba(229, 192, 123, 0.05)",
    highlight: "rgba(229, 192, 123, 0.25)",
    textStrong: "#ffffff",
    textMuted: "#a1a1aa",
  },
  "botanical": {
    id: "botanical",
    name: "Botanical Sage",
    primary: "#2d5a43",
    primaryForeground: "#ffffff",
    secondary: "#e2e8e4",
    secondaryForeground: "#1b3628",
    accent: "#b85437",
    accentForeground: "#ffffff",
    background: "#faf8f5",
    foreground: "#1a202c",
    muted: "#edf2f0",
    mutedForeground: "#4a5568",
    border: "#cbd5e0",
    surfaceElevated: "rgba(255, 255, 255, 0.9)",
    surfaceTinted: "rgba(45, 90, 67, 0.04)",
    highlight: "rgba(45, 90, 67, 0.15)",
    textStrong: "#1a202c",
    textMuted: "#4a5568",
  },
  "academic-royal": {
    id: "academic-royal",
    name: "Collegiate Royal",
    primary: "#1e3a8a",
    primaryForeground: "#ffffff",
    secondary: "#f1f5f9",
    secondaryForeground: "#0f172a",
    accent: "#b45309",
    accentForeground: "#ffffff",
    background: "#fdfdfe",
    foreground: "#0f172a",
    muted: "#f8fafc",
    mutedForeground: "#475569",
    border: "#e2e8f0",
    surfaceElevated: "rgba(255, 255, 255, 0.9)",
    surfaceTinted: "rgba(30, 58, 138, 0.03)",
    highlight: "rgba(180, 83, 9, 0.15)",
    textStrong: "#0f172a",
    textMuted: "#475569",
  },
  "studio-light": {
    id: "studio-light",
    name: "Architectural Studio",
    primary: "#18181b",
    primaryForeground: "#ffffff",
    secondary: "#f4f4f5",
    secondaryForeground: "#1d4ed8",
    accent: "#1d4ed8",
    accentForeground: "#ffffff",
    background: "#ffffff",
    foreground: "#18181b",
    muted: "#f4f4f5",
    mutedForeground: "#52525b",
    border: "#e4e4e7",
    surfaceElevated: "rgba(255, 255, 255, 0.95)",
    surfaceTinted: "rgba(24, 24, 27, 0.02)",
    highlight: "rgba(29, 78, 216, 0.15)",
    textStrong: "#09090b",
    textMuted: "#52525b",
  },
  "neon-minimal": {
    id: "neon-minimal",
    name: "Cyber Minimal",
    primary: "#7c3aed",
    primaryForeground: "#ffffff",
    secondary: "#1f2937",
    secondaryForeground: "#22d3ee",
    accent: "#22d3ee",
    accentForeground: "#082f49",
    background: "#08090d",
    foreground: "#f3f4f6",
    muted: "#111827",
    mutedForeground: "#9ca3af",
    border: "#374151",
    isDark: true,
    surfaceElevated: "rgba(31, 41, 55, 0.7)",
    surfaceTinted: "rgba(124, 58, 237, 0.05)",
    highlight: "rgba(34, 211, 238, 0.25)",
    textStrong: "#ffffff",
    textMuted: "#9ca3af",
  },
  "obsidian-executive": {
    id: "obsidian-executive",
    name: "Obsidian Executive",
    isDark: true,
    surface0: "#090d16",
    surface1: "#111726",
    surface2: "#182032",
    surfaceTinted: "rgba(229, 185, 100, 0.05)",
    borderSubtle: "rgba(255, 255, 255, 0.08)",
    borderStrong: "rgba(255, 255, 255, 0.14)",
    textPrimary: "#f8fafc",
    textSecondary: "#cbd5e1",
    textTertiary: "#8492a6",
    accent: "#e5c07b",
    accentForeground: "#090d16",
    focus: "#e5c07b",
    effects: {
      level: "subtle",
      glass: true,
      glow: false,
      hoverLift: true,
      ambientBackground: false,
    },
    primary: "#e5c07b",
    primaryForeground: "#090d16",
    secondary: "#111726",
    secondaryForeground: "#f8fafc",
    background: "#090d16",
    foreground: "#f8fafc",
    muted: "#182032",
    mutedForeground: "#8492a6",
    border: "#232d42",
    surfaceElevated: "#111726",
    highlight: "rgba(229, 192, 123, 0.15)",
    textStrong: "#f8fafc",
    textMuted: "#cbd5e1",
  },
  "sapphire-scholar": {
    id: "sapphire-scholar",
    name: "Sapphire Scholar",
    isDark: false,
    surface0: "#f8fafc",
    surface1: "#ffffff",
    surface2: "#f1f5f9",
    surfaceTinted: "rgba(37, 99, 235, 0.05)",
    borderSubtle: "#e2e8f0",
    borderStrong: "#cbd5e1",
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textTertiary: "#64748b",
    accent: "#2563eb",
    accentForeground: "#ffffff",
    focus: "#2563eb",
    effects: {
      level: "subtle",
      glass: false,
      glow: false,
      hoverLift: true,
      ambientBackground: false,
    },
    primary: "#2563eb",
    primaryForeground: "#ffffff",
    secondary: "#0f172a",
    secondaryForeground: "#ffffff",
    background: "#f8fafc",
    foreground: "#0f172a",
    muted: "#f1f5f9",
    mutedForeground: "#475569",
    border: "#e2e8f0",
    surfaceElevated: "#ffffff",
    highlight: "rgba(37, 99, 235, 0.15)",
    textStrong: "#0f172a",
    textMuted: "#475569",
  },
  "cyber-teal": {
    id: "cyber-teal",
    name: "Cyber Teal",
    isDark: true,
    surface0: "#090d16",
    surface1: "#0f172a",
    surface2: "#162032",
    surfaceTinted: "rgba(6, 182, 212, 0.08)",
    borderSubtle: "rgba(6, 182, 212, 0.2)",
    borderStrong: "#0891b2",
    textPrimary: "#f8fafc",
    textSecondary: "#94a3b8",
    textTertiary: "#64748b",
    accent: "#06b6d4",
    accentForeground: "#090d16",
    focus: "#06b6d4",
    effects: {
      level: "expressive",
      glass: true,
      glow: true,
      hoverLift: true,
      ambientBackground: true,
    },
    primary: "#06b6d4",
    primaryForeground: "#090d16",
    secondary: "#0f172a",
    secondaryForeground: "#f8fafc",
    background: "#090d16",
    foreground: "#f8fafc",
    muted: "#162032",
    mutedForeground: "#94a3b8",
    border: "#0e7490",
    surfaceElevated: "#0f172a",
    highlight: "rgba(6, 182, 212, 0.25)",
    textStrong: "#f8fafc",
    textMuted: "#94a3b8",
  },
};

export const FONT_PAIRINGS: Record<string, FontPairingConfig> = {
  "serif-classic": {
    id: "serif-classic",
    name: "Classical Academic (Cinzel + Lora)",
    headingFont: "'Cinzel', serif",
    bodyFont: "'Lora', serif",
    monoFont: "'JetBrains Mono', monospace",
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Lora:ital,wght@0,400;0,600;1,400&family=JetBrains+Mono:wght@400;500&display=swap",
  },
  "modern-sans": {
    id: "modern-sans",
    name: "Modern Executive (Plus Jakarta Sans + Inter)",
    headingFont: "'Plus Jakarta Sans', sans-serif",
    bodyFont: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    monoFont: "'JetBrains Mono', monospace",
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap",
  },
  editorial: {
    id: "editorial",
    name: "Editorial (Playfair Display + Source Serif)",
    headingFont: "'Playfair Display', Georgia, serif",
    bodyFont: "'Source Serif 4', Georgia, serif",
    monoFont: "'JetBrains Mono', monospace",
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,400&family=Source+Serif+4:ital,wght@0,400;0,600;1,400&family=JetBrains+Mono:wght@400;500&display=swap",
  },
  technical: {
    id: "technical",
    name: "Technical Scholar (Space Grotesk + Plex Sans)",
    headingFont: "'Space Grotesk', sans-serif",
    bodyFont: "'IBM Plex Sans', sans-serif",
    monoFont: "'JetBrains Mono', monospace",
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600&family=Space+Grotesk:wght@600;700&family=JetBrains+Mono:wght@400;500&display=swap",
  },
  minimal: {
    id: "minimal",
    name: "Swiss Minimal (Outfit + Inter)",
    headingFont: "'Outfit', sans-serif",
    bodyFont: "'Inter', sans-serif",
    monoFont: "'JetBrains Mono', monospace",
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Outfit:wght@500;600;700&family=JetBrains+Mono:wght@400;500&display=swap",
  },
  "nordic-clean": {
    id: "nordic-clean",
    name: "Nordic Clean (DM Sans + DM Mono)",
    headingFont: "'DM Sans', sans-serif",
    bodyFont: "'DM Sans', sans-serif",
    monoFont: "'DM Mono', monospace",
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=DM+Sans:ital,opsz,wght@0,9..40,400..800;1,9..40,400..800&display=swap",
  },
  "heritage-press": {
    id: "heritage-press",
    name: "Heritage Press (Cormorant Garamond + Proza Libre)",
    headingFont: "'Cormorant Garamond', Georgia, serif",
    bodyFont: "'Proza Libre', sans-serif",
    monoFont: "'JetBrains Mono', monospace",
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,400&family=Proza+Libre:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap",
  },
  "cyber-modern": {
    id: "cyber-modern",
    name: "Cyber Modern (Syne + Cabinet Grotesk)",
    headingFont: "'Syne', sans-serif",
    bodyFont: "'Inter', sans-serif",
    monoFont: "'JetBrains Mono', monospace",
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Syne:wght@600;700;800&family=JetBrains+Mono:wght@400;500&display=swap",
  },
};

export function getPalette(id?: string): ColorPalette {
  if (!id) return COLOR_PALETTES["ocean-blue"];
  return COLOR_PALETTES[id] || COLOR_PALETTES["ocean-blue"];
}

export function getFontPairing(id?: string): FontPairingConfig {
  if (!id) return FONT_PAIRINGS["serif-classic"];
  const normalizedId = id === "technical-mono" ? "technical" : id;
  return FONT_PAIRINGS[normalizedId] || FONT_PAIRINGS["serif-classic"];
}

